import { ObjectMap } from "../entities/object-map";

function isObject(obj:any): obj is {} {
    return !!obj && typeof obj === 'object';
}

type ArrayCallback<T = any> = (item: T, key?: number, iterable?: T[]) => void | boolean;
type ObjectCallback<T = any> = (item: T, key?: string, iterable?: ObjectMap<T>) => void | boolean;
type StringCallback = (item: string, key?: number, iterable?: string) => void | boolean;
export function forEach<T>(array: T[], callback: ArrayCallback<T>): void;
export function forEach<T>(obj: ObjectMap<T>, callback: ObjectCallback<T>): void;
export function forEach(s: string, callback: StringCallback): void;
export function forEach(iterable:any[] | string | ObjectMap, callback:ArrayCallback | ObjectCallback | StringCallback): void {

    const cb = callback as (item?: any, key?: number | any, iterable?: any) => void | boolean;
    // map
    if(!Array.isArray(iterable) && isObject(iterable)) {
        let obj = iterable as ObjectMap;
        for(const key in obj) {
            if(obj.hasOwnProperty(key)) {
                if(cb(obj[key], key, obj) === false) { return; }
            }
        }
    } // array and string
    else {
        let s = iterable as string | any[];
        for(let i = 0, ln = s.length; i < ln; i++) {
            if(cb(s[i], i, s) === false) { return; }
        }
    }
}

export function firstOrDefault<T>(collection:T[], predicate:(item:T)=>boolean): T | null {
    let result:T | null = null;
    forEach(collection, item => {
        if(predicate(item) === true) {
            result = item;
            return false; // break
        }
    });

    return result;
}

export function where<T>(collection:T[], predicate:(item:T)=>boolean):T[] {
    let result:T[] = [];
    forEach(collection, item => {
        if(predicate(item) === true) {
            result.push(item);
        }
    });

    return result;
}

export function firstIndexWhere<T>(collection:T[], predicate:(item:T)=>boolean):number {
    let result = -1;
    forEach(collection, (item, index) => {
        if(predicate(item) === true) {
            result = index as number;
            return false; // break
        }
    });

    return result;
}

export function indicesWhere<T>(collection:T[], predicate:(item:T)=>boolean):number[] {
    let result:number[] = [];
    forEach(collection, (item, index) => {
        if(predicate(item) === true) {
            let i = index as number;
            result.push(i);
        }
    });

    return result;
}

/**
 * Creates a new array of items based on the given array, where each item in the new array produced a unique value
 * when applied to a comparer function
 * @param collection Array of items to extract distinct values from
 * @param comparer Function each array item is applied to. The values returned are compared to determine the 'uniqueness' of the item.
 * This function must be idempotent.
 * Objects are considered to be the same if they return equal values, even if they are different instances
 * @returns New array of unique items
 */
export function distinct<T>(collection: T[], comparer: (item:T) => any): T[] {
    if(collection.length === 0) { return []; }
    if(collection.length === 1) { return [...collection] };

    const hashset: any[] = [];
    const result: T[] = [];
    forEach(collection, item => {
        const hash = comparer(item);
        if(hashset.indexOf(hash) === -1) {
            hashset.push(hash);
            result.push(item);
        }
    });

    return result;
}

/**
 * Returns a copy of the given array, with the first occurence of a member matching the given predicate removed.
 * This function does not alter the original array
 * @param collection 
 * @param predicate 
 * @returns 
 */
export function remove<T>(collection:T[], predicate:(item:T)=>boolean): T[] {
    let result = [...collection];
    let index = firstIndexWhere(collection, predicate);
    if(index !== -1) {
        result.splice(index, 1);
    }

    return result;
} 

/**
 * Returns a copy of the given array, with the member at the given index removed.
 * This function does not alter the original array
 * @param collection 
 * @param index 
 * @returns 
 */
export function removeAt<T>(collection:T[], index: number): T[] {
    let result = [...collection];
    if(index >= 0 && index < result.length) {
        result.splice(index, 1);
    }

    return result;
} 

/**
 * Returns a copy of the given array, with all members matching the given predicate removed.
 * This function does not alter the original array
 * @param collection 
 * @param predicate 
 * @returns 
 */
export function removeAll<T>(collection:T[], predicate:(item:T)=>boolean): T[] {
    let result = [...collection];
    let index  = 0;
    while(index > -1) {
        index = firstIndexWhere(result, predicate);
        if(index > -1) { 
            result = remove(result, predicate);
        }
    }

    return result;
} 

export function any<T>(collection:T[], predicate:(item:T)=>boolean):boolean {
    let result = firstOrDefault(collection, predicate);
    return result != null;
}

export function none<T>(collection:T[], predicate:(item:T)=>boolean):boolean {
    return !any(collection, predicate);
}

export function groupBy<T>(collection: T[], aggregator: (item: T) => number | string | null | undefined): ObjectMap<T[]> {
    const groups: ObjectMap<T[]> = {};
    forEach(collection, item => {
        const agg = aggregator(item);
        let key = '';
        if(agg === null) { key = 'null'; }
        else if(agg === undefined) { key = 'undefined'; }
        else {
            key = agg.toString();
        }

        if(!groups[key]) { groups[key] = []; }
        groups[key].push(item);
    });

    return groups;
}

export function orderBy<T>(collection:T[], predicate:(item:T) => number|string):T[] {
    if (!collection || collection.length < 2) { return collection; }
    
    // map to simple list of string/number
    let sorter: Array<string | number | T> = collection.map(predicate);

    //sort the list
    if (typeof sorter[0] === 'string') {
        (sorter as string[]).sort();
    }
    else {
        (sorter as number[]).sort((a, b) => { return a - b; });
    }

    //sort the array elements
    forEach(collection, (item) => {
        let i = sorter.indexOf(predicate(item));
        sorter[i] = item;
    });

    return sorter as T[];
}

export function orderByDesc<T>(collection:T[], predicate:(item:T) => number|string):T[] {
    return orderBy(collection, predicate).reverse();
}

export function sum<T>(collection:T[], predicate:(item:T)=>number):number {
    let total = 0;
    let values = collection.map(predicate);
    forEach(values, v =>{
        total += v;
    });

    return total;
}

export function min<T>(collection:T[], predicate:(item:T) => number): number {
    let m: number | undefined = undefined;
    let values = collection.map(predicate);
    forEach(values, v =>{
        (m === undefined || v < m) && (m = v);
    });

    return m !== undefined ? m : NaN;
}

export function max<T>(collection:T[], predicate:(item:T) => number): number {
    let m: number | undefined = undefined;
    let values = collection.map(predicate);
    forEach(values, v =>{
        (m === undefined || v > m) && (m = v);
    });

    return m !== undefined ? m : NaN;
}

export function aggregateSum<T>(collection:T[], valueDerivator:(item:T) => number, aggregator: (item: T) => number | string | null | undefined): ObjectMap<number> {
    const aggregatedCollection = groupBy(collection, aggregator);
    const aggregatedSum: ObjectMap<number> = {};

    forEach(aggregatedCollection, (items, key) => {
        aggregatedSum[key!] = sum(items, valueDerivator);
    });

    return aggregatedSum;
}

export function partition<T>(collection:T[], partitionSize: number): T[][] {
    const partitions: T[][] = [];
    let taken = 0;
    for(let i = 0; (i * partitionSize) < collection.length; i++) {
        const skip = i * partitionSize;
        partitions.push(collection.slice(skip, skip + partitionSize));
        taken += partitionSize;
    }
    if(taken < collection.length) {
        partitions.push(collection.slice(taken));
    }

    return partitions;
}

export function replaceFirstWhere<T>(collection:T[], predicate:(item:T)=>boolean, replacement: T, insertIfNotFound: boolean = false): T[] {
    const result = [...collection];
    const index = firstIndexWhere(result, predicate);
    if(index >= 0) {
        result.splice(index, 1, replacement);
    } else if(insertIfNotFound) {
        result.push(replacement);
    }

    return result;
}

// it is not advisable to use this when T is an object type, as the object reference will be used for all replacements
export function replaceAllWhere<T>(collection:T[], predicate:(item:T)=>boolean, replacement: T):T[] {
    const result = [...collection];
    const indices = indicesWhere(collection, predicate);
    forEach(indices, (index) => {
        result.splice(index, 1, replacement);
    });

    return result;
}
